import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
  effect,
  inject,
  input
} from '@angular/core';
import { SnackBarService } from '@core/services/snackbar.service';
import { copyToClipboard } from '@modules/projects/modules/project-summary-v2/project-summary.helper';
import { TranslateService } from '@ngx-translate/core';

@Directive({
  selector: '[copyInline]'
})
export class CopyInlineDirective implements AfterViewInit, OnDestroy {
  readonly copyInline = input<string | null | undefined>('');
  readonly copyInlineIconSrc = input('/assets/img/ic-copy_to_clipboard_blue.svg');
  readonly copyInlineMessageKey = input('Contacts.contactDataCopied');
  readonly copyInlineIconAlt = input('Copy value');

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);

  private iconWrapper: HTMLSpanElement | null = null;
  private iconImage: HTMLImageElement | null = null;
  private removeListeners: Array<() => void> = [];

  constructor() {
    effect(() => {
      const iconImage = this.iconImage;
      const iconWrapper = this.iconWrapper;
      if (!iconImage || !iconWrapper) return;

      const hasValue = !!this.copyInline();
      this.renderer.setAttribute(iconImage, 'src', this.copyInlineIconSrc());
      this.renderer.setAttribute(iconImage, 'alt', this.copyInlineIconAlt());
      this.renderer.setStyle(iconWrapper, 'pointer-events', hasValue ? 'auto' : 'none');
    });
  }

  ngAfterViewInit(): void {
    this.setupHost();
    this.createIcon();
    this.attachListeners();
    this.hideIcon();
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((removeListener) => removeListener());
    this.removeListeners = [];
    this.iconWrapper = null;
    this.iconImage = null;
  }

  private setupHost(): void {
    const host = this.host.nativeElement;
    this.renderer.setStyle(host, 'display', 'flex');
    this.renderer.setStyle(host, 'align-items', 'center');
    this.renderer.setStyle(host, 'max-width', '100%');
    this.renderer.setStyle(host, 'min-width', '0');
  }

  private createIcon(): void {
    const iconWrapper = this.renderer.createElement('span') as HTMLSpanElement;
    const iconImage = this.renderer.createElement('img') as HTMLImageElement;

    this.renderer.setStyle(iconWrapper, 'align-items', 'center');
    this.renderer.setStyle(iconWrapper, 'justify-content', 'center');
    this.renderer.setStyle(iconWrapper, 'cursor', 'pointer');
    this.renderer.setStyle(iconWrapper, 'flex', '0 0 auto');

    this.renderer.setStyle(iconImage, 'width', '14px');
    this.renderer.setStyle(iconImage, 'height', '14px');
    this.renderer.setStyle(iconImage, 'display', 'block');

    this.renderer.appendChild(iconWrapper, iconImage);
    this.renderer.appendChild(this.host.nativeElement, iconWrapper);

    this.iconWrapper = iconWrapper;
    this.iconImage = iconImage;
  }

  private attachListeners(): void {
    const host = this.host.nativeElement;
    const iconWrapper = this.iconWrapper;

    if (!iconWrapper) return;

    this.removeListeners.push(this.renderer.listen(host, 'mouseenter', () => this.showIcon()));
    this.removeListeners.push(this.renderer.listen(host, 'mouseleave', () => this.hideIcon()));
    this.removeListeners.push(this.renderer.listen(host, 'focusin', () => this.showIcon()));
    this.removeListeners.push(this.renderer.listen(host, 'focusout', () => this.hideIcon()));
    this.removeListeners.push(
      this.renderer.listen(iconWrapper, 'click', (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        void this.handleCopyClick();
      })
    );
  }

  private async handleCopyClick(): Promise<void> {
    const copied = await copyToClipboard(this.copyInline() || '');
    if (!copied) return;

    this.snackBarService.success(this.translateService.instant(this.copyInlineMessageKey()));
  }

  private showIcon(): void {
    if (!this.iconWrapper || !this.copyInline()) return;

    this.renderer.setStyle(this.iconWrapper, 'display', 'inline-flex');
    this.renderer.setStyle(this.iconWrapper, 'margin-left', '6px');
  }

  private hideIcon(): void {
    if (!this.iconWrapper) return;

    this.renderer.setStyle(this.iconWrapper, 'display', 'none');
    this.renderer.setStyle(this.iconWrapper, 'margin-left', '0');
  }
}
